/**
 * Registry of all available validators
 * Each validator can format input and validate values
 */

import type { Validator, ValidatorOptions } from './types'

// Basic Validators
const required: Validator = {
  name: 'required',
  message: 'This field is required',
  validate: (value: string) => {
    const isValid = value !== null && value !== undefined && value.trim() !== ''
    return {
      isValid,
      message: isValid ? undefined : 'This field is required'
    }
  }
}

const email: Validator = {
  name: 'email',
  message: 'Please enter a valid email address',
  validate: (value: string) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const isValid = pattern.test(value)
    return {
      isValid,
      message: isValid ? undefined : 'Please enter a valid email address'
    }
  }
}

// Format Validators with Auto-formatting
const phoneNumber: Validator = {
  name: 'phoneNumber',
  message: 'Phone number must be in format (XXX) XXX-XXXX',
  validate: (value: string) => {
    const pattern = /^\(\d{3}\) \d{3}-\d{4}$/
    const isValid = pattern.test(value)
    return {
      isValid,
      message: isValid ? undefined : 'Phone number must be in format (XXX) XXX-XXXX'
    }
  },
  formatInput: (value: string) => {
    const digits = value.replace(/\D/g, '')
    if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    }
    return digits
  }
}

const ssnWithDashes: Validator = {
  name: 'ssnWithDashes',
  message: 'SSN must be in format XXX-XX-XXXX',
  validate: (value: string) => {
    const pattern = /^\d{3}-\d{2}-\d{4}$/
    const isValid = pattern.test(value)
    return {
      isValid,
      message: isValid ? undefined : 'SSN must be in format XXX-XX-XXXX'
    }
  },
  formatInput: (value: string) => {
    const digits = value.replace(/\D/g, '')
    if (digits.length >= 5) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`
    } else if (digits.length >= 3) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`
    }
    return digits
  }
}

const fedTaxIdWithDashes: Validator = {
  name: 'fedTaxIdWithDashes',
  message: 'Federal Tax ID must be in format XX-XXXXXXX',
  validate: (value: string) => {
    const pattern = /^\d{2}-\d{7}$/
    const isValid = pattern.test(value)
    return {
      isValid,
      message: isValid ? undefined : 'Federal Tax ID must be in format XX-XXXXXXX'
    }
  },
  formatInput: (value: string) => {
    const digits = value.replace(/\D/g, '')
    if (digits.length >= 2) {
      return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`
    }
    return digits
  }
}

const zipCode: Validator = {
  name: 'zipCode',
  message: 'ZIP code must be 5 digits or 5+4 format (XXXXX or XXXXX-XXXX)',
  validate: (value: string) => {
    const pattern = /^\d{5}(-\d{4})?$/
    const isValid = pattern.test(value)
    return {
      isValid,
      message: isValid ? undefined : 'ZIP code must be 5 digits or 5+4 format (XXXXX or XXXXX-XXXX)'
    }
  }
}

// Business Validators
const currency: Validator = {
  name: 'currency',
  message: 'Please enter a valid currency amount',
  validate: (value: string, options: ValidatorOptions = { min: 0, max: Infinity }) => {
    const cleanValue = value.replace(/[$,]/g, '')
    const numValue = parseFloat(cleanValue)
    
    if (isNaN(numValue)) {
      return { isValid: false, message: 'Please enter a valid currency amount' }
    }
    
    if (numValue < options.min) {
      return { isValid: false, message: `Amount must be at least $${options.min.toLocaleString()}` }
    }
    
    if (numValue > options.max) {
      return { isValid: false, message: `Amount cannot exceed $${options.max.toLocaleString()}` }
    }
    
    return { isValid: true }
  },
  formatInput: (value: string) => {
    const digits = value.replace(/[^\d.]/g, '')
    const number = parseFloat(digits)
    return isNaN(number) ? '' : number.toLocaleString()
  }
}

// Date Validators
const dateOver18Years: Validator = {
  name: 'dateOver18Years',
  message: 'Must be at least 18 years old',
  validate: (value: string) => {
    if (!value) return { isValid: true } // Allow empty if not required
    
    const birthDate = new Date(value)
    const today = new Date()
    const age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    
    const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) 
      ? age - 1 
      : age
      
    const isValid = actualAge >= 18
    return {
      isValid,
      message: isValid ? undefined : 'Must be at least 18 years old'
    }
  }
}

// Length Validators
const minLength: Validator = {
  name: 'minLength',
  message: 'Input is too short',
  validate: (value: string, options: ValidatorOptions = { length: 1 }) => {
    const isValid = value.length >= options.length
    return {
      isValid,
      message: isValid ? undefined : `Must be at least ${options.length} characters`
    }
  }
}

const maxLength: Validator = {
  name: 'maxLength', 
  message: 'Input is too long',
  validate: (value: string, options: ValidatorOptions = { length: 255 }) => {
    const isValid = value.length <= options.length
    return {
      isValid,
      message: isValid ? undefined : `Cannot exceed ${options.length} characters`
    }
  }
}

// Pattern Validators
const alphaOnly: Validator = {
  name: 'alphaOnly',
  message: 'Only letters are allowed',
  validate: (value: string) => {
    const pattern = /^[a-zA-Z\s]*$/
    const isValid = pattern.test(value)
    return {
      isValid,
      message: isValid ? undefined : 'Only letters and spaces are allowed'
    }
  }
}

const numericOnly: Validator = {
  name: 'numericOnly',
  message: 'Only numbers are allowed',
  validate: (value: string) => {
    const pattern = /^\d*$/
    const isValid = pattern.test(value)
    return {
      isValid,
      message: isValid ? undefined : 'Only numbers are allowed'
    }
  }
}

const wholeNumberOnly: Validator = {
  name: 'wholeNumberOnly',
  message: 'Only whole numbers are allowed (no decimals)',
  validate: (value: string) => {
    // Allow empty values (let required validator handle that)
    if (!value || value.trim() === '') {
      return { isValid: true }
    }
    
    // Check if the value contains a decimal point
    if (value.includes('.')) {
      return {
        isValid: false,
        message: 'Only whole numbers are allowed (no decimals)'
      }
    }
    
    // Check if it's a valid whole number
    const pattern = /^\d+$/
    const isValid = pattern.test(value.trim())
    
    return {
      isValid,
      message: isValid ? undefined : 'Only whole numbers are allowed (no decimals)'
    }
  },
  formatInput: (value: string) => {
    // Remove any non-digit characters including decimal points
    return value.replace(/[^\d]/g, '')
  }
}

const alphanumeric: Validator = {
  name: 'alphanumeric',
  message: 'Only letters and numbers are allowed',
  validate: (value: string) => {
    const pattern = /^[a-zA-Z0-9\s]*$/
    const isValid = pattern.test(value)
    return {
      isValid,
      message: isValid ? undefined : 'Only letters, numbers, and spaces are allowed'
    }
  }
}

// URL Validator
const url: Validator = {
  name: 'url',
  message: 'Please enter a valid URL',
  validate: (value: string) => {
    if (!value) return { isValid: true } // Allow empty if not required
    
    try {
      new URL(value)
      return { isValid: true }
    } catch {
      return { isValid: false, message: 'Please enter a valid URL (e.g., https://example.com)' }
    }
  }
}

// Registry of all validators
export const VALIDATORS: Record<string, Validator> = {
  // Basic
  required,
  email,
  
  // Format
  phoneNumber,
  ssnWithDashes,
  fedTaxIdWithDashes,
  zipCode,
  
  // Business
  currency,
  
  // Date
  dateOver18Years,
  
  // Length
  minLength,
  maxLength,
  
  // Pattern
  alphaOnly,
  numericOnly,
  wholeNumberOnly,
  alphanumeric,
  
  // URL
  url
}

// Helper function to get a validator by name
export const getValidator = (name: string): Validator | undefined => {
  return VALIDATORS[name]
}

// Helper function to get all validator names
export const getValidatorNames = (): string[] => {
  return Object.keys(VALIDATORS)
}
